// The rules for a display name, which both builds must apply alike (g68): an
// account's name now, and a guest's name once g69 lands. A name is what other
// people see beside something a person created, so it is text to render, never
// an identifier: two people may choose the same one.
//
// Pure, like everything under shared/. Normalisation is Unicode's own stable
// NFC, and every class tested below is one whose membership Unicode never
// changes for an assigned character, so Node and a browser on different
// Unicode versions give the same verdict. That is why unassigned code points
// are not refused: which ones are unassigned depends on the engine.

/** Counted in code points after normalisation, so an accented letter is one. */
export const DISPLAY_NAME_MAX = 40;

/** Refused before normalising, so an enormous string costs nothing to judge. */
const RAW_MAX = 400;

export type DisplayNameError = "invalid_characters" | "too_long";

export type DisplayNameResult =
  | { ok: true; name: string | null }
  | { ok: false; code: DisplayNameError };

/**
 * Control characters (tab and newline among them), lone surrogates, line and
 * paragraph separators, and format characters -- bidirectional overrides, the
 * byte-order mark, soft hyphens -- except the two joiners. Zero-width
 * non-joiner and joiner are how Persian and Indic names spell, and how an
 * emoji sequence holds together, so refusing them would refuse real names.
 */
const REFUSED = /[\p{Cc}\p{Cs}\p{Zl}\p{Zp}]|(?![\u200C\u200D])\p{Cf}/u;

/**
 * What is left when only spacing and default-ignorable code points were
 * typed: joiners, variation selectors, the combining grapheme joiner, Hangul
 * fillers. Each renders as nothing, so a name of only these would label a row
 * with a blank. The property is fixed for assigned code points.
 */
const INVISIBLE = /^[\p{Zs}\p{Default_Ignorable_Code_Point}]*$/u;

/**
 * Trims, applies NFC and collapses each run of spaces to one. A name with
 * nothing visible left is `null`: no name, which an account may have.
 */
export function normalizeDisplayName(raw: string): DisplayNameResult {
  if (raw.length > RAW_MAX) return { ok: false, code: "too_long" };
  const composed = raw.normalize("NFC");
  if (REFUSED.test(composed)) return { ok: false, code: "invalid_characters" };
  const name = composed.replace(/\p{Zs}+/gu, " ").trim();
  if (INVISIBLE.test(name)) return { ok: true, name: null };
  if ([...name].length > DISPLAY_NAME_MAX) return { ok: false, code: "too_long" };
  return { ok: true, name };
}
